import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["features", "shouldFillContainer"];
import React, { useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@atlassian/search-dialog';
import { useProductContext } from '../product-router';
import { mergeRefCallback } from '../../utils/merge-ref-callback';
import { useQuery } from '../query-context';
import { useDialogExpansionContext } from '../dialog-expansion-context';
import { useABTest } from '../ab-test-context';
import { ProductSearchInputSkeleton, ProductSearchInputSkeletonInteractive } from '../../common/product-search-input-skeleton';
import { GenericProductSearchInput } from './generic-product-search-input';
import { useSessionUserInput } from '../user-input-provider';
import { useIntl } from 'react-intl-next';
import { messages } from '../../messages';
export var ActiveProductSearchInputStateless = function ActiveProductSearchInputStateless(_ref) {
  var forwardRef = _ref.forwardRef,
    isExpanded = _ref.isExpanded,
    setIsExpanded = _ref.setIsExpanded,
    activeProductId = _ref.activeProductId,
    query = _ref.query,
    setQuery = _ref.setQuery,
    onNavigateGeneric = _ref.onNavigateGeneric,
    isLoading = _ref.isLoading,
    pageLoadMetrics = _ref.pageLoadMetrics,
    shouldFillContainer = _ref.shouldFillContainer;
  var inputRef = useRef(null);
  useEffect(function () {
    if (pageLoadMetrics) {
      var metric = pageLoadMetrics.metric,
        customDataGetter = pageLoadMetrics.customDataGetter;
      metric.stop({
        customData: customDataGetter()
      });
    }
  }, [pageLoadMetrics]);
  useEffect(function () {
    if (isExpanded) {
      var _inputRef$current;
      inputRef === null || inputRef === void 0 || (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    }
  }, [isExpanded, inputRef, activeProductId]);
  var ref = mergeRefCallback(forwardRef, inputRef);
  var onOpen = useCallback(function () {
    return setIsExpanded(true);
  }, [setIsExpanded]);
  var onBack = useCallback(function () {
    setIsExpanded(false);
  }, [setIsExpanded]);
  var inputComponentProps = {
    activeProductId: activeProductId,
    query: query,
    setQuery: setQuery,
    forwardRef: ref,
    isExpanded: isExpanded,
    setIsExpanded: setIsExpanded,
    onOpen: onOpen,
    onNavigateGeneric: onNavigateGeneric,
    onBack: onBack,
    isLoading: isLoading,
    shouldFillContainer: shouldFillContainer
  };
  return /*#__PURE__*/React.createElement(GenericProductSearchInput, inputComponentProps);
};

/**
 * A search input component made for a cross product experience. This input
 * changes based off the active product's registered properties.
 */
export var ActiveProductSearchInput = function ActiveProductSearchInput(_ref2) {
  var features = _ref2.features,
    shouldFillContainer = _ref2.shouldFillContainer,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var _useProductContext = useProductContext(),
    getActiveProduct = _useProductContext.getActiveProduct;
  var _useQuery = useQuery(),
    query = _useQuery.query,
    setQuery = _useQuery.setQuery,
    isLoading = _useQuery.isLoading;
  var _useDialogExpansionCo = useDialogExpansionContext(),
    isExpanded = _useDialogExpansionCo.isExpanded,
    setIsExpanded = _useDialogExpansionCo.setIsExpanded;
  var _useSessionUserInput = useSessionUserInput(),
    stickySearchEnabled = _useSessionUserInput.stickySearchEnabled;
  var abTest = useABTest();
  var theme = useTheme();
  var activeProduct = getActiveProduct();
  var intl = useIntl();
  if (!abTest) {
    return features !== null && features !== void 0 && features.interactiveSkeleton ? /*#__PURE__*/React.createElement(ProductSearchInputSkeletonInteractive, _extends({
      theme: theme,
      shouldFillContainer: shouldFillContainer,
      stickySearchEnabled: stickySearchEnabled
    }, features.interactiveSkeleton)) : /*#__PURE__*/React.createElement(ProductSearchInputSkeleton, {
      shouldFillContainer: shouldFillContainer,
      theme: theme,
      placeholder: intl.formatMessage(messages.common_search_input_collapsed_placeholder)
    });
  }
  return /*#__PURE__*/React.createElement(ActiveProductSearchInputStateless, _extends({}, rest, {
    shouldFillContainer: shouldFillContainer,
    activeProductId: activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.id,
    query: query,
    setQuery: setQuery,
    isExpanded: isExpanded,
    setIsExpanded: setIsExpanded,
    isLoading: isLoading
  }));
};